export function wrapIndex(index, length) {
  return length > 0 ? ((index % length) + length) % length : 0
}
export function relativePosition(index, active, length) {
  let position = wrapIndex(index - active, length)
  if (position > length / 2) position -= length
  return position
}
export function mountCarousel(root) {
  const abort = new AbortController()
  const options = { signal: abort.signal }
  const slides = [...root.querySelectorAll('[data-campaign-slide]')]
  const previous = root.querySelector('[data-carousel-previous]')
  const next = root.querySelector('[data-carousel-next]')
  const status = root.querySelector('[data-carousel-status]')
  const stage = root.querySelector('[data-carousel-stage]')
  let active = 0
  let gesture
  let suppressClick = false
  const render = (announce = false) => {
    slides.forEach((slide, index) => {
      const position = relativePosition(index, active, slides.length)
      slide.dataset.position = String(position)
      slide.setAttribute('aria-hidden', position === 0 ? 'false' : 'true')
      slide.inert = Math.abs(position) > 3
      slide.style.zIndex = String(10 - Math.abs(position))
      const surface = slide.querySelector('[data-carousel-video-surface]')
      if (surface) surface.tabIndex = position === 0 ? 0 : -1
      slide.querySelectorAll('video').forEach((video) => {
        video.controls = position === 0
        if (position === 0) {
          if (video.preload === 'none') {
            video.preload = 'metadata'
            video.load?.()
          }
        } else {
          video.pause()
        }
      })
    })
    if (status && announce) status.textContent = active + 1 + ' / ' + slides.length
    if (previous) previous.disabled = slides.length < 2
    if (next) next.disabled = slides.length < 2
  }
  const move = (amount, announce = true) => {
    active = wrapIndex(active + amount, slides.length)
    render(announce)
  }
  previous?.addEventListener(
    'click',
    () => {
      move(-1)
    },
    options,
  )
  next?.addEventListener(
    'click',
    () => {
      move(1)
    },
    options,
  )
  slides.forEach((slide, index) => {
    const video = slide.querySelector('video')
    const surface = slide.querySelector('[data-carousel-video-surface]')
    if (video && surface) {
      const updatePressed = () => surface.setAttribute('aria-pressed', String(!video.paused))
      video.addEventListener('play', updatePressed, options)
      video.addEventListener('pause', updatePressed, options)
      surface.addEventListener(
        'click',
        () => {
          if (index !== active) return
          if (video.paused) video.play()?.catch?.(() => {})
          else video.pause()
        },
        options,
      )
      updatePressed()
    }
    slide.addEventListener(
      'click',
      () => {
        if (index === active || slide.inert) return
        const video = slide.querySelector('video')
        active = index
        render(true)
        const playPromise = video?.play?.()
        playPromise?.catch?.(() => {})
      },
      options,
    )
  })
  root.addEventListener(
    'keydown',
    (event) => {
      if (event.target.closest('video,input,textarea,select')) return
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault()
        move(event.key === 'ArrowLeft' ? -1 : 1)
        if (event.target.closest('[data-carousel-video-surface]')) {
          slides[active]
            .querySelector('[data-carousel-video-surface]')
            ?.focus({ preventScroll: true })
        }
      }
    },
    options,
  )
  root.addEventListener(
    'shopify:block:select',
    (event) => {
      const index = slides.findIndex((slide) => slide.contains(event.target))
      if (index >= 0) {
        active = index
        render(true)
      }
    },
    options,
  )
  stage?.addEventListener(
    'dragstart',
    (event) => {
      if (event.target.closest('img')) event.preventDefault()
    },
    options,
  )
  stage?.addEventListener(
    'pointerdown',
    (event) => {
      suppressClick = false
      if (event.isPrimary === false || event.button > 0) return
      if (
        event.target.closest('button,a,input,textarea,select') &&
        !event.target.closest('[data-carousel-video-surface]')
      )
        return
      const video = event.target.closest('video')
      // Leave the native seek, volume and fullscreen controls to the player.
      if (video?.controls && event.clientY >= video.getBoundingClientRect().bottom - 48) return
      gesture = { x: event.clientX, y: event.clientY, id: event.pointerId }
    },
    { ...options, capture: true },
  )
  stage?.addEventListener(
    'pointermove',
    (event) => {
      if (!gesture || gesture.id !== event.pointerId) return
      const dx = Math.abs(event.clientX - gesture.x)
      const dy = Math.abs(event.clientY - gesture.y)
      if (!gesture.dragging && dx > 10 && dx > dy) {
        gesture.dragging = true
        stage.setPointerCapture?.(event.pointerId)
      }
      if (gesture.dragging) event.preventDefault()
    },
    { ...options, capture: true, passive: false },
  )
  stage?.addEventListener(
    'pointerup',
    (event) => {
      if (!gesture || gesture.id !== event.pointerId) return
      const dx = event.clientX - gesture.x,
        dy = event.clientY - gesture.y
      gesture = undefined
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        suppressClick = true
        event.preventDefault()
        move(dx < 0 ? 1 : -1)
      }
      if (stage.hasPointerCapture?.(event.pointerId)) stage.releasePointerCapture(event.pointerId)
    },
    { ...options, capture: true },
  )
  stage?.addEventListener(
    'pointercancel',
    () => {
      gesture = undefined
    },
    { ...options, capture: true },
  )
  stage?.addEventListener(
    'click',
    (event) => {
      if (!suppressClick || event.detail === 0) return
      suppressClick = false
      event.preventDefault()
      event.stopImmediatePropagation()
    },
    { ...options, capture: true },
  )
  root.setAttribute('data-enhanced', '')
  render()
  return () => {
    abort.abort()
    slides.forEach((slide) => slide.querySelectorAll('video').forEach((video) => video.pause()))
    root.removeAttribute('data-enhanced')
  }
}
if (typeof customElements !== 'undefined' && !customElements.get('campaign-media-carousel')) {
  customElements.define(
    'campaign-media-carousel',
    class extends HTMLElement {
      connectedCallback() {
        this.disconnectCarousel ??= mountCarousel(this)
      }
      disconnectedCallback() {
        this.disconnectCarousel?.()
        this.disconnectCarousel = undefined
      }
    },
  )
}
